import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import toast from 'react-hot-toast';
import { Mail, Lock, LogIn } from 'lucide-react';

const LOGO_WHITE_BG = 'https://customer-assets.emergentagent.com/job_b0e0e0df-2d06-4315-be1e-7d0387fd3a85/artifacts/eslg6eoj_imagen%282%29.png';
const LOGO_DARK_BG = 'https://customer-assets.emergentagent.com/job_b0e0e0df-2d06-4315-be1e-7d0387fd3a85/artifacts/pks6y868_imagen%283%29.png';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('¡Bienvenido!');
      navigate('/welcome');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Panel izquierdo - Logo + brand (oculto en móvil) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-slate-900 via-[#0f1a3b] to-[#1e3a8a] items-center justify-center p-12 overflow-hidden">
        {/* Patrón decorativo */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(44,87,197,0.4) 0px, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.05) 0px, transparent 50%)',
        }} />
        <div className="relative z-10 text-center max-w-md">
          <img
            src={LOGO_DARK_BG}
            alt="SIEMPRE"
            className="w-72 h-72 mx-auto object-contain drop-shadow-2xl"
            data-testid="login-logo-dark"
          />
          <p className="mt-6 text-slate-300 text-sm tracking-wider uppercase">Portal de Pagos</p>
          <p className="mt-2 text-slate-400 text-xs">Calidad en productos y servicio</p>
        </div>
      </div>

      {/* Panel derecho - Formulario */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Logo solo visible en móvil */}
          <div className="lg:hidden text-center mb-6">
            <img
              src={LOGO_WHITE_BG}
              alt="SIEMPRE"
              className="w-32 h-32 mx-auto object-contain"
              data-testid="login-logo-mobile"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#1e3a8a] to-[#2c57c5] bg-clip-text text-transparent" data-testid="login-title">
                Bienvenido
              </h1>
              <p className="text-slate-500 text-sm">Inicia sesión en tu cuenta</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 focus-visible:ring-[#2c57c5]"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 focus-visible:ring-[#2c57c5]"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#2c57c5] hover:from-[#1e3a8a] hover:to-[#1e40af] text-white shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  'Iniciando sesión...'
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Iniciar Sesión
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-xs text-slate-400">
              SIEMPRE &middot; Calidad en productos y servicio
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
